import { supabase, SupabaseNotConfiguredError } from "../supabase";
import type { DeckRow } from "../db";
import type { Database } from "../database.types";

type DeckDbRow = Database["public"]["Tables"]["decks"]["Row"];
type DeckInsert = Database["public"]["Tables"]["decks"]["Insert"];

function rowToDeck(row: DeckDbRow & { sync_version?: number }): DeckRow {
  return {
    syncVersion: row.sync_version,
    id: row.id,
    name: row.name ?? "",
    createdAt: new Date(row.created_at).getTime(),
    sourceLang: (row.source_lang as DeckRow["sourceLang"]) ?? undefined,
    targetLang: (row.target_lang as DeckRow["targetLang"]) ?? undefined,
    topic: (row.topic as DeckRow["topic"]) ?? undefined,
    ageGroup: (row.age_group as DeckRow["ageGroup"]) ?? undefined,
    level: (row.level as DeckRow["level"]) ?? undefined,
  };
}

function deckToInsert(userId: string, deck: DeckRow): DeckInsert {
  return {
    id: deck.id,
    owner_id: userId,
    name: deck.name,
    source_lang: deck.sourceLang ?? null,
    target_lang: deck.targetLang ?? null,
    topic: deck.topic ?? null,
    age_group: deck.ageGroup ?? null,
    level: deck.level ?? null,
    created_at: new Date(deck.createdAt).toISOString(),
  };
}

const CHUNK_SIZE = 500;

export async function pushDecks(userId: string, decks: DeckRow[]): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  for (let i = 0; i < decks.length; i += CHUNK_SIZE) {
    const chunk = decks.slice(i, i + CHUNK_SIZE).map((d) => deckToInsert(userId, d));
    const { error } = await supabase.from("decks").upsert(chunk, { onConflict: "owner_id,id" });
    if (error) throw error;
  }
}

export async function pushDeck(userId: string, deck: DeckRow): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase
    .from("decks")
    .upsert(deckToInsert(userId, deck), { onConflict: "owner_id,id" });
  if (error) throw error;
}

export async function pullDecks(userId: string): Promise<DeckRow[]> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const rows: DeckDbRow[] = [];
  let after: string | undefined;
  for (;;) {
    let query = supabase.from("decks").select("*")
      .eq("owner_id", userId).order("id").limit(500);
    if (after !== undefined) query = query.gt("id", after);
    const { data, error } = await query;
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data ?? []).length < 500) break;
    after = data![data!.length - 1].id;
  }
  return rows.map(rowToDeck);
}

export async function deleteDeckRemote(userId: string, id: string): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase.from("decks").delete().eq("id", id).eq("owner_id", userId);
  if (error) throw error;
}
